import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Clock, Loader2, Play, Download, CalendarClock, RotateCcw, Mail } from 'lucide-react';

const humanize = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const relTime = (iso) => {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch (e) { return ''; }
};

// Scheduled backups for one knowledge kind ('coach' | 'explore').
export default function KnowledgeBackups({ kind, testIdPrefix }) {
  const [cfg, setCfg] = useState({ enabled: false, frequency: 'daily', keep: 5, email_digest: true });
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState('');
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, b] = await Promise.all([api.getKnowledgeBackupConfig(kind), api.listKnowledgeBackups(kind)]);
      setCfg({ enabled: !!c.enabled, frequency: c.frequency || 'daily', keep: c.keep || 5, email_digest: c.email_digest !== false });
      setBackups(b.items || []);
    } catch (e) { /* non-blocking */ } finally { setLoaded(true); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    const merged = { ...cfg, ...next };
    setCfg(merged);
    try {
      await api.setKnowledgeBackupConfig({ kind, enabled: merged.enabled, frequency: merged.frequency, keep: Number(merged.keep), email_digest: merged.email_digest });
      toast.success('Backup schedule saved');
    } catch (e) { toast.error(e.message || 'Could not save schedule'); }
  };

  const runNow = async () => {
    setBusy('run');
    try {
      await api.runKnowledgeBackup(kind);
      toast.success('Backup created');
      await load();
    } catch (e) { toast.error(e.message || 'Backup failed'); } finally { setBusy(''); }
  };

  const download = async (b) => {
    try {
      const pkg = await api.downloadKnowledgeBackup(b.backup_id);
      const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-${kind}-backup-${(b.created_at || '').slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || 'Download failed'); }
  };

  const restore = async (b) => {
    const total = Object.values(b.counts || {}).reduce((x, y) => x + y, 0);
    if (typeof window !== 'undefined' && !window.confirm(`Restore this backup (${total} item${total === 1 ? '' : 's'})? Existing knowledge is merged, not deleted, and this can be undone.`)) return;
    setBusy('restore-' + b.backup_id);
    try {
      const res = await api.restoreKnowledgeBackup(b.backup_id);
      const ins = Object.values(res.summary || {}).reduce((x, y) => x + (y.inserted || 0), 0);
      const upd = Object.values(res.summary || {}).reduce((x, y) => x + (y.updated || 0), 0);
      toast.success(`Restored — ${ins} added, ${upd} updated`);
    } catch (e) { toast.error(e.message || 'Restore failed'); } finally { setBusy(''); }
  };

  if (!loaded) return null;

  return (
    <div className="pt-2 border-t border-border/40" data-testid={`${testIdPrefix}-backups`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Scheduled backups</p>
        <Button size="sm" variant="outline" onClick={runNow} disabled={!!busy} data-testid={`${testIdPrefix}-backup-run`}>
          {busy === 'run' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
          Back up now
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" className="accent-primary w-4 h-4" checked={cfg.enabled} onChange={(e) => save({ enabled: e.target.checked })} data-testid={`${testIdPrefix}-backup-enabled`} />
          Auto-backup
        </label>
        <select value={cfg.frequency} onChange={(e) => save({ frequency: e.target.value })} disabled={!cfg.enabled} className="text-sm rounded-md border border-border bg-background px-2 py-1" data-testid={`${testIdPrefix}-backup-frequency`}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Keep
          <input type="number" min="1" max="20" value={cfg.keep} onChange={(e) => setCfg((c) => ({ ...c, keep: e.target.value }))} onBlur={(e) => save({ keep: e.target.value })} className="w-14 text-sm rounded-md border border-border bg-background px-2 py-1" data-testid={`${testIdPrefix}-backup-keep`} />
          versions
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground">
          <input type="checkbox" className="accent-primary w-4 h-4" checked={cfg.email_digest} disabled={!cfg.enabled} onChange={(e) => save({ email_digest: e.target.checked })} data-testid={`${testIdPrefix}-backup-digest`} />
          <Mail className="w-3 h-3" /> Email me a summary
        </label>
      </div>
      {backups.length > 0 ? (
        <div className="mt-2 space-y-1" data-testid={`${testIdPrefix}-backup-list`}>
          {backups.map((b) => {
            const total = Object.values(b.counts || {}).reduce((x, y) => x + y, 0);
            return (
              <div key={b.backup_id} className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  {relTime(b.created_at)} · {total} item{total === 1 ? '' : 's'}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${b.trigger === 'scheduled' ? 'bg-blue-500/15 text-blue-400' : 'bg-muted text-muted-foreground'}`}>{b.trigger}</span>
                </span>
                <span className="flex items-center gap-3">
                  <button onClick={() => restore(b)} disabled={!!busy} className="flex items-center gap-1 text-primary hover:underline disabled:opacity-50" data-testid={`${testIdPrefix}-backup-restore`}>
                    {busy === 'restore-' + b.backup_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Restore
                  </button>
                  <button onClick={() => download(b)} className="flex items-center gap-1 text-primary hover:underline" data-testid={`${testIdPrefix}-backup-download`}>
                    <Download className="w-3 h-3" /> Download
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground mt-2" data-testid={`${testIdPrefix}-backup-empty`}>No backups yet.</p>
      )}
    </div>
  );
}
